import { useForm, Link } from "@inertiajs/react";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Download, FileUp } from "lucide-react";
import { useRef } from "react";

const UPLOAD_LABELS: Record<string, string> = {
  collection_import: "Collection Import",
  producer_import: "Import producers",
  bottle_import: "Import bottles",
};

const REQUIRES_FILE_REPORTS = new Set([
  "collection_import",
  "producer_import",
  "bottle_import",
]);

type Props = {
  reports: string[];
  flash?: { notice?: string; alert?: string };
};

export default function UploadReport({ reports, flash }: Props) {
  useFlashToast(flash);

  const form = useForm<{ report: string; file: File | null }>({
    report: "",
    file: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const requiresFile = REQUIRES_FILE_REPORTS.has(form.data.report);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.post("/reports/upload", {
      forceFormData: true,
    });
  }

  function handleReportChange(value: string) {
    form.setData((prev) => ({ ...prev, report: value, file: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div className="py-8 px-4">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/">
            <ArrowLeft className="size-4" />
            Back to home
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileUp className="size-5 text-amber-600" />
              <CardTitle>Upload Report</CardTitle>
            </div>
            <CardDescription>
              Select a report type and upload a CSV file. The import will be
              processed in the background.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="report">Report Type</Label>
                <Select
                  value={form.data.report}
                  onValueChange={handleReportChange}
                >
                  <SelectTrigger id="report">
                    <SelectValue placeholder="Select a report type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {reports.map((report) => (
                      <SelectItem key={report} value={report}>
                        {UPLOAD_LABELS[report] ?? report}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.data.report && (
                <div className="rounded-md border border-dashed p-4">
                  <p className="text-sm text-muted-foreground">
                    Need the correct format? Download a blank template CSV with
                    the expected column headers.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    asChild
                  >
                    <a href={`/reports/template?report=${form.data.report}`}>
                      <Download className="size-4" />
                      Download Template
                    </a>
                  </Button>
                </div>
              )}

              {requiresFile && (
                <div className="space-y-2">
                  <Label htmlFor="file">CSV File</Label>
                  <Input
                    ref={fileInputRef}
                    id="file"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) =>
                      form.setData("file", e.target.files?.[0] ?? null)
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum file size: 25 MB
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button
                type="submit"
                disabled={
                  form.processing ||
                  !form.data.report ||
                  (requiresFile && !form.data.file)
                }
              >
                {form.processing ? "Uploading…" : "Upload Report"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
